import {
	type AttachmentInfo,
	type AttachmentListResponse,
	type AttachmentsDeleteRequest,
	OrganisationAttachmentControllerApiFactory,
} from '@api-client'
import { CHECKBOX_COL_DEF, DataGrid } from '@components/DataGrid'
import { Input } from '@components/Input'
import { Loading } from '@components/Loading'
import { formatDate } from '@helpers/dates'
import { QueryKey } from '@helpers/enums'
import { useAsyncApi } from '@hooks/useAsyncApi'
import { DeleteOutlined, Search } from '@mui/icons-material'
import {
	Box,
	Button,
	IconButton,
	InputAdornment,
	Modal,
	Typography,
} from '@mui/material'
import type {
	GridColDef,
	GridRowParams,
	GridRowSelectionModel,
} from '@mui/x-data-grid-premium'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { useSnackbar } from 'notistack'
import React, {
	type ChangeEvent,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react'
import { useTranslation } from 'react-i18next'

import * as S from './styles'

interface IProps {
	isModalOpened: boolean
	selectedFiles: readonly AttachmentInfo[]
	onConfirm: (file: readonly AttachmentInfo[]) => void
	onModalClose: (bool: boolean) => void
}

interface IUploadFileButton {
	onUploadFile: (event: ChangeEvent<HTMLInputElement>) => void
}

const clearField = (event: React.MouseEvent<HTMLInputElement>) => {
	const target = event.currentTarget
	target.value = ''
}

const UploadFileButton = ({ onUploadFile }: IUploadFileButton) => {
	const { t } = useTranslation()
	return (
		<>
			<S.FileUploadInput
				id="file-upload"
				name="file"
				accept="application/pdf"
				type="file"
				onChange={onUploadFile}
				onClick={clearField}
			/>
			<label htmlFor="file-upload">
				<Button variant="outlined" color="primary" component="span">
					{t('button.uploadFile')}
				</Button>
			</label>
		</>
	)
}

type Primitive = string | number | boolean | undefined | null
type DeepReadonly<T> = T extends Primitive
	? T
	: T extends (infer U)[]
		? DeepReadonlyArray<U>
		: DeepReadonlyObject<T>
type DeepReadonlyArray<T> = readonly DeepReadonly<T>[]
type DeepReadonlyObject<T> = {
	readonly [P in keyof T]: DeepReadonly<T[P]>
}

const isNewId = (id: string) => id.split('_')[0] === 'new'

const getErrorMessage = (
	status: number,
	responseData: unknown,
	t: (key: string) => string
): string => {
	if (typeof responseData === 'string') {
		switch (responseData) {
			case 'pdfContainsJavaScript':
				return t('toast.error.pdfContainsJavaScript')
			case 'pdfCorrupted':
				return t('toast.error.pdfCorrupted')
			case 'pdfMalformed':
				return t('toast.error.pdfMalformed')
			case 'pdfPasswordProtected':
				return t('toast.error.pdfPasswordProtected')
		}
	}
	switch (status) {
		case 406:
			return t('toast.error.invalidFile')
		case 413:
			return t('toast.error.fileTooLarge')
		default:
			return t('toast.error.uploadFailed')
	}
}

const addFile = (
	files: readonly AttachmentInfo[],
	fileObject: File
): readonly AttachmentInfo[] => {
	const file = {
		id: 'new_' + Math.random(),
		name: fileObject.name,
		createdAt: formatDate(new Date().toString()),
		isUsed: false,
	}
	return [file, ...files]
}

const DEFAULT_PAGINATION = { page: 0, pageSize: 10 }
const EMPTY_ITEMS: AttachmentInfo[] = []

const SelectFileModal = ({
	isModalOpened,
	selectedFiles,
	onModalClose,
	onConfirm,
}: IProps) => {
	const { t } = useTranslation()

	const [data, setData] = useState<
		DeepReadonly<AttachmentListResponse> | undefined
	>()
	const [searchTerm, setSearchTerm] = useState<string>('')
	const [selected, setSelected] =
		useState<readonly AttachmentInfo[]>(selectedFiles)
	const [paginationModel, setPaginationModel] = useState(DEFAULT_PAGINATION)

	const { enqueueSnackbar } = useSnackbar()

	const organisationAttachmentControllerApi = useAsyncApi(
		OrganisationAttachmentControllerApiFactory
	)

	const {
		data: filesData,
		isError,
		isLoading,
		refetch,
	} = useQuery({
		queryKey: [
			QueryKey.ListAttachments,
			paginationModel.page,
			paginationModel.pageSize,
			searchTerm,
		],
		queryFn: () =>
			organisationAttachmentControllerApi(api =>
				api.listAttachments(
					paginationModel.page,
					paginationModel.pageSize,
					searchTerm
				)
			),
		placeholderData: keepPreviousData,
	})

	const rowSelectionModel = useMemo(
		(): GridRowSelectionModel => ({
			type: 'include',
			ids: new Set(selected.map(f => f.id)),
		}),
		[selected]
	)

	const onRowSelectionModelChange = useCallback(
		(model: GridRowSelectionModel) => {
			const items = data?.data ?? EMPTY_ITEMS
			const newSelected =
				model.type === 'exclude'
					? items.filter(item => !model.ids.has(item.id))
					: items.filter(item => model.ids.has(item.id))
			setSelected(newSelected)
		},
		[data]
	)

	const isRowSelectable = useCallback(
		({ row }: GridRowParams<AttachmentInfo>) => !isNewId(row.id),
		[]
	)

	const columns = useMemo(
		(): readonly GridColDef<AttachmentInfo>[] => [
			CHECKBOX_COL_DEF,
			{
				field: 'name',
				headerName: t('selectFileModal.tableHeaderDocumentName'),
				flex: 1,
				display: 'flex',
				renderCell: ({ row }) =>
					isNewId(row.id) ? (
						<Box display="flex" p={1.2} justifyContent="center">
							<Loading size="small" noWrapper />
						</Box>
					) : (
						<S.NameWrapper>{row.name}</S.NameWrapper>
					),
				sortable: false,
			},
			{
				field: 'createdAt',
				headerName: t('selectFileModal.tableHeaderUploadDate'),
				flex: 1,
				display: 'flex',
				valueFormatter: value => formatDate(value),
				sortable: false,
			},
		],
		[t]
	)

	const onUploadFile = useCallback(
		async (event: ChangeEvent<HTMLInputElement>) => {
			const target = event.target
			const fileObject = (target.files as FileList)[0]

			if (!fileObject || !filesData) {
				return
			}

			const files: readonly AttachmentInfo[] = filesData.data.data
			const { metadata } = filesData.data
			const newFiles = addFile(files, fileObject)

			setData({
				data: newFiles,
				metadata: { ...metadata, count: newFiles.length },
			})

			try {
				const response = await organisationAttachmentControllerApi(api =>
					api.uploadAttachment(fileObject)
				)
				if (response.status === 200) {
					refetch()
				}
			} catch (err) {
				if (isAxiosError(err)) {
					setData({
						data: files,
						metadata: { ...metadata, count: files.length },
					})
					const status = err.response?.status ?? 500
					const responseData = err.response?.data
					const errorMessage = getErrorMessage(status, responseData, t)
					enqueueSnackbar(errorMessage, { variant: 'error' })
				}
			}
		},
		[
			enqueueSnackbar,
			refetch,
			t,
			organisationAttachmentControllerApi,
			filesData,
		]
	)

	const onDelete = useCallback(async () => {
		const filesSelected: AttachmentsDeleteRequest = {
			ids: selected.map(({ id }) => id),
		}
		try {
			const files = await organisationAttachmentControllerApi(api =>
				api.deleteAttachments(filesSelected)
			)
			if (files.status === 200) {
				refetch()
				setSelected(EMPTY_ITEMS)
			}
		} catch {
			// Ignore errors on deletion.
		}
	}, [organisationAttachmentControllerApi, refetch, selected])

	const onSearch = useCallback(
		(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const target: HTMLInputElement = event.target as HTMLInputElement
			setSearchTerm(target.value)
		},
		[]
	)

	const onCancel = useCallback(() => {
		setSearchTerm('')
		onModalClose(false)
	}, [onModalClose])

	const onConfirmSelection = useCallback(() => {
		onConfirm(selected)
		onCancel()
	}, [onConfirm, selected, onCancel])

	useEffect(() => {
		setData(filesData?.data)
	}, [filesData])

	useEffect(() => {
		setSelected(selectedFiles)
	}, [selectedFiles])

	return (
		<Modal open={isModalOpened} onClose={onCancel}>
			<S.BackdropWithModalStyles>
				{isLoading && <Loading />}
				{isError && !data && <h1>{t('error')}</h1>}
				{data && (
					<>
						<Typography variant="h2">{t('selectFileModal.title')}</Typography>
						<Box
							display="flex"
							alignItems="center"
							justifyContent="space-between"
							mt={3}
							mb={2}
						>
							<Box width={300}>
								<Input
									variant="filled"
									fullWidth
									value={searchTerm}
									name="Search"
									placeholder={t('selectFileModal.searchDocument')}
									InputProps={{
										endAdornment: (
											<InputAdornment position="end">
												<Search />
											</InputAdornment>
										),
									}}
									onChange={onSearch}
								/>
							</Box>
							<Box display="flex" alignItems="center" gap={1}>
								<IconButton onClick={onDelete} disabled={selected.length === 0}>
									<DeleteOutlined />
								</IconButton>
								<Typography
									variant="subtitle2"
									component="h5"
									color="textSecondary"
								>
									{t('selectFileModal.maxFileSize')}
								</Typography>
								<UploadFileButton onUploadFile={onUploadFile} />
							</Box>
						</Box>
						<Box height={400}>
							<DataGrid
								rows={data.data as AttachmentInfo[]}
								columns={columns}
								checkboxSelection
								disableRowSelectionOnClick
								rowSelectionModel={rowSelectionModel}
								onRowSelectionModelChange={onRowSelectionModelChange}
								isRowSelectable={isRowSelectable}
								disableColumnMenu
								pagination
								paginationMode="server"
								rowCount={data.metadata.count}
								paginationModel={paginationModel}
								onPaginationModelChange={setPaginationModel}
								loading={isLoading}
							/>
						</Box>
						<Box display="flex" justifyContent="flex-end" pt={1.5}>
							<Box mr={2}>
								<Button variant="outlined" color="primary" onClick={onCancel}>
									{t('button.cancel')}
								</Button>
							</Box>
							<Button
								variant="contained"
								color="primary"
								disabled={selected.length === 0}
								onClick={onConfirmSelection}
							>
								{t('button.confirm')}
							</Button>
						</Box>
					</>
				)}
			</S.BackdropWithModalStyles>
		</Modal>
	)
}

export { SelectFileModal }
